import { acceptDeadline, DELIVERY_METHODS, orderStage, type DeliveryMethod } from '@guildite/domain/orders';

import type { OrderMessage, OrderView } from '../ports.ts';

const text = (value: unknown) => (typeof value === 'string' ? value : '');
const minor = (value: unknown) => (Number.isFinite(Number(value)) ? Math.round(Number(value)) : 0);
// Legacy orders keep decimal cedis; v2 orders also carry amounts in minor units.
const cedisToMinor = (value: unknown) => Math.round(Number(value || 0) * 100);

function media(url: unknown): string | null {
  const value = text(url);
  return value.startsWith('https://') || value.startsWith('http://127.0.0.1:') ? value : null;
}

export function orderFromRecord(id: string, data: Record<string, unknown>): OrderView {
  const amounts = (data.amounts ?? {}) as Record<string, unknown>;
  const items = Array.isArray(data.items) ? data.items as Record<string, unknown>[] : [];
  const subtotalMinor = amounts.subtotalMinor !== undefined ? minor(amounts.subtotalMinor) : cedisToMinor(data.subtotal);
  const feeMinor = amounts.buyerPlatformFeeMinor !== undefined ? minor(amounts.buyerPlatformFeeMinor) : cedisToMinor(data.tax);
  const deliveryMinor = amounts.deliveryMinor !== undefined ? minor(amounts.deliveryMinor) : cedisToMinor(data.deliveryFee);
  const method = DELIVERY_METHODS.includes(data.deliveryMethod as DeliveryMethod) ? data.deliveryMethod as DeliveryMethod : null;
  const history = Array.isArray(data.history) ? data.history as Record<string, unknown>[] : [];
  const rejection = [...history].reverse().find(entry => entry.to === 'Rejected');
  // A deleted shop's handle may now belong to another shop, so it is dropped.
  const shopDeleted = Boolean(data.deletedShop);
  return {
    id,
    stage: orderStage(data.status),
    status: text(data.status) || 'Pending',
    shopId: text(data.shopId) || text(data.sellerId),
    shopName: shopDeleted ? 'Deleted shop' : text(data.shopName) || text(data.sellerName) || 'Shop',
    shopHandle: shopDeleted ? null : text(data.shopHandle) || null,
    ...(shopDeleted ? { shopDeleted } : {}),
    buyerId: text(data.buyerId),
    buyerName: text(data.buyerName) || 'Buyer',
    items: items.map(item => ({
      listingId: text(item.id),
      name: text(item.name) || 'Item',
      photo: media(item.image),
      quantity: Math.max(1, minor(item.quantity)),
      unitMinor: item.unitMinor !== undefined ? minor(item.unitMinor) : cedisToMinor(item.price),
      summary: text(item.variantSummary),
      note: text(item.note),
    })),
    fulfilment: data.deliveryType === 'pickup' ? 'pickup' : 'delivery',
    deliveryMethod: method,
    dropArea: text(data.dropArea) || null,
    promoCode: text(data.promoCode) || null,
    stay: data.stay === true,
    deliveryZone: text(data.deliveryZone) || null,
    amounts: {
      subtotalMinor, feeMinor, deliveryMinor,
      discountMinor: minor(amounts.discountMinor ?? 0),
      dealDiscountMinor: minor(amounts.dealDiscountMinor ?? 0),
      deliveryDiscountMinor: minor(amounts.deliveryDiscountMinor ?? 0),
      totalMinor: amounts.buyerTotalMinor !== undefined ? minor(amounts.buyerTotalMinor) : cedisToMinor(data.total),
      sellerItemsMinor: amounts.sellerItemsPayableMinor !== undefined ? minor(amounts.sellerItemsPayableMinor) : subtotalMinor,
    },
    createdAt: text(data.createdAt),
    paidAt: text(data.paidAt) || text(data.paymentPaidAt) || null,
    // Only open hours count (orders keep the shop's hours from checkout).
    acceptBy: data.acceptHours && data.paidAt ? new Date(acceptDeadline(data) ?? 0).toISOString() : text(data.acceptBy) || null,
    scheduledFor: text(data.scheduledFor) || null,
    opensAt: text(data.opensAt) || null,
    refundOwedMinor: Number(data.refundOwedMinor) > 0 ? minor(data.refundOwedMinor) : null,
    rejectionReason: text(data.rejectReason) || text(rejection?.reason) || null,
    riderName: text(data.riderName) || null,
    codeLockUntil: Number(data.codeLockUntil) > 0 ? Number(data.codeLockUntil) : null,
    cancelledBy: text(data.cancelledBy) || null,
    reviewed: data.hasReview === true,
    history: history.filter(entry => text(entry.to) && text(entry.at)).map(entry => ({ to: text(entry.to), at: text(entry.at) })),
  };
}

// Unpaid checkouts stay hidden until Paystack confirms the payment.
export function isPlacedOrder(data: Record<string, unknown>): boolean {
  return data.v === 2 && Boolean(data.paidAt || data.paymentPaidAt);
}

export function messageFromRecord(id: string, data: Record<string, unknown>): OrderMessage {
  const role = data.authorRole === 'shop' || data.authorRole === 'rider' ? data.authorRole : 'buyer';
  return {
    id, authorId: text(data.authorId), authorRole: role, authorName: text(data.authorName) || 'Someone',
    text: text(data.text), createdAt: text(data.createdAt), scamWarning: data.scamWarning === true,
  };
}
